import React, { useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import { Alert, Badge } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import {
  fetchPlatformOrganizationMetrics,
  type PlatformOrganizationMetricsResponse,
  type PlatformOrganizationRenewal,
} from "../../api/platformMetrics";
import { formatDate, formatDaysFromNow } from "../organizations/utils";
import { statusVariant } from "../organizations/constants";
import "./OverviewPage.css";

const usdFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function parsePeriodEnd(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isPremiumProfessional(r: PlatformOrganizationRenewal): boolean {
  return r.planId === "professional";
}

function periodRelativeClass(periodEnd: Date | null): string {
  if (!periodEnd) return "overview-renewal-card__period-relative";
  const diffDays = Math.ceil((periodEnd.getTime() - Date.now()) / 86400000);
  if (diffDays < 0) {
    return "overview-renewal-card__period-relative overview-renewal-card__period-relative--overdue";
  }
  if (diffDays <= 7) {
    return "overview-renewal-card__period-relative overview-renewal-card__period-relative--soon";
  }
  return "overview-renewal-card__period-relative";
}

interface MetricTileProps {
  accent: "live" | "paying" | "trial" | "premium";
  label: string;
  value: number;
  hint: string;
}

const MetricTile = ({ accent, label, value, hint }: MetricTileProps) => (
  <article className={`overview-metric-tile overview-metric-tile--${accent}`}>
    <div className="overview-metric-tile__label">{label}</div>
    <div className="overview-metric-tile__value">{value}</div>
    <p className="overview-metric-tile__hint">{hint}</p>
  </article>
);

interface RenewalCardProps {
  org: PlatformOrganizationRenewal;
}

const RenewalCard = ({ org }: RenewalCardProps) => {
  const status = org.subscriptionStatus ?? "unknown";
  const variant = statusVariant[status] ?? "secondary";
  const premium = isPremiumProfessional(org);
  const periodEnd = parsePeriodEnd(org.currentPeriodEnd);
  const inRevenue = org.countsTowardRevenue === true;

  return (
    <article className="overview-renewal-card">
      <header className="overview-renewal-card__header">
        <h3 className="overview-renewal-card__name">{org.name}</h3>
        <div className="overview-renewal-card__badges">
          <Badge bg={variant} className="text-uppercase">
            {status}
          </Badge>
          {inRevenue ? (
            <Badge bg="success">Revenue</Badge>
          ) : status === "active" ? (
            <Badge bg="warning" text="dark">
              Grace
            </Badge>
          ) : null}
          {premium ? <Badge bg="primary">Premium</Badge> : null}
          {org.intelligence ? (
            <Badge bg={org.intelligence.trialing ? "info" : "dark"}>
              Intel {org.intelligence.capacityId}
              {org.intelligence.trialing ? " trial" : ""}
            </Badge>
          ) : null}
        </div>
      </header>
      <div className="overview-renewal-card__body">
        <div className="overview-renewal-card__plan-row">
          <span className="overview-renewal-card__plan-name">{org.planName}</span>
          {org.monthlyUsd != null ? (
            <span className="overview-renewal-card__price">
              {usdFormatter.format(org.monthlyUsd)}/mo
            </span>
          ) : (
            <span className="text-muted">—/mo</span>
          )}
        </div>
        {org.paymentMethod ? (
          <div className="overview-renewal-card__payment">
            {org.paymentMethod}
          </div>
        ) : null}
      </div>
      <footer className="overview-renewal-card__footer">
        <div>
          <p className="overview-renewal-card__period-label">
            {premium ? "Premium renewal" : "Period end"}
          </p>
          <p className="overview-renewal-card__period-date">
            {formatDate(periodEnd)}
          </p>
        </div>
        <span className={periodRelativeClass(periodEnd)}>
          {formatDaysFromNow(periodEnd)}
        </span>
      </footer>
    </article>
  );
};

interface OverviewPageProps {
  user: User;
}

const OverviewPage: React.FC<OverviewPageProps> = ({ user }) => {
  const [data, setData] = useState<PlatformOrganizationMetricsResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPlatformOrganizationMetrics(user)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load overview",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const metrics = data ?? {
    totalActivePlans: 0,
    payingActive: 0,
    inTrial: 0,
    expiredPaying: 0,
    expiredTrialing: 0,
    premiumProfessional: 0,
    totalMonthlyUsd: 0,
    monthlyUsdExcluded: 0,
    excludedTestOrgs: 0,
    byPlan: {} as Record<string, number>,
    renewals: [] as PlatformOrganizationRenewal[],
    intelligence: {
      active: 0,
      trialing: 0,
      total: 0,
      monthlyUsd: 0,
      byCapacity: {} as Record<string, number>,
    },
  };

  const intel = metrics.intelligence ?? {
    active: 0,
    trialing: 0,
    total: 0,
    monthlyUsd: 0,
    byCapacity: {} as Record<string, number>,
  };

  const byPlanEntries = Object.entries(metrics.byPlan).filter(
    ([, n]) => n > 0,
  );
  const byIntelEntries = Object.entries(intel.byCapacity || {}).filter(
    ([, n]) => n > 0,
  );

  const expiredTotal =
    (metrics.expiredPaying ?? 0) + (metrics.expiredTrialing ?? 0);

  const revenueRenewals = useMemo(
    () =>
      metrics.renewals.filter((org) => org.countsTowardRevenue === true),
    [metrics.renewals],
  );

  return (
    <div className="overview-page">
      <header className="content__header overview-page__intro">
        <div>
          <h1 className="page-header__title">Platform overview</h1>
          <p className="page-header__lead">
            Live subscriptions and estimated MRR. Revenue counts only production{" "}
            <strong>active</strong> plans with a valid period (sandbox/test orgs
            excluded).
          </p>
        </div>
        <div className="content__header-actions">
          <NavLink
            to="/organizations"
            className="btn btn-outline-primary btn-sm"
          >
            Organizations
          </NavLink>
        </div>
      </header>

      {error ? (
        <Alert variant="danger" className="border-0 shadow-sm">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="overview-loading" role="status" aria-live="polite">
          <div className="overview-loading__spinner" aria-hidden="true" />
          <span>Loading organization data…</span>
        </div>
      ) : (
        <>
          <section className="overview-metrics-grid" aria-label="Key metrics">
            <MetricTile
              accent="live"
              label="Live subs"
              value={metrics.totalActivePlans}
              hint="Active or trial, period not ended"
            />
            <MetricTile
              accent="paying"
              label="Paying"
              value={metrics.payingActive}
              hint="Active with valid period end"
            />
            <MetricTile
              accent="trial"
              label="In trial"
              value={metrics.inTrial}
              hint="Trialing before end date"
            />
            <MetricTile
              accent="premium"
              label="Professional"
              value={metrics.premiumProfessional}
              hint="Live on premium plan"
            />
          </section>

          {(metrics.excludedTestOrgs ?? 0) > 0 ? (
            <div className="overview-alert-expired" role="status">
              <span className="overview-alert-expired__icon" aria-hidden="true">
                i
              </span>
              <div>
                <strong>{metrics.excludedTestOrgs}</strong> sandbox/test org
                {(metrics.excludedTestOrgs ?? 0) === 1 ? "" : "s"} excluded
                from counts and revenue.
              </div>
            </div>
          ) : null}

          {expiredTotal > 0 ? (
            <div className="overview-alert-expired" role="status">
              <span className="overview-alert-expired__icon" aria-hidden="true">
                !
              </span>
              <div>
                <strong>{expiredTotal}</strong> org
                {expiredTotal === 1 ? "" : "s"} excluded — expired period or
                ended trial.
                {metrics.expiredPaying ? (
                  <> {metrics.expiredPaying} paying.</>
                ) : null}
                {metrics.expiredTrialing ? (
                  <>
                    {" "}
                    {metrics.expiredTrialing} trial
                    {metrics.expiredTrialing === 1 ? "" : "s"} ended.
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          <section className="overview-revenue-hero" aria-label="Estimated revenue">
            <div className="overview-revenue-hero__eyebrow">
              Est. monthly revenue
            </div>
            <div className="overview-revenue-hero__amount">
              {usdFormatter.format(metrics.totalMonthlyUsd)}
            </div>
            <p className="overview-revenue-hero__meta">
              From <strong>{metrics.payingActive}</strong> production paying org
              {metrics.payingActive === 1 ? "" : "s"} · sum of{" "}
              <code>plan.monthlyPrice</code>. Sandbox/test, trials, and overdue
              periods excluded.
            </p>
            {metrics.monthlyUsdExcluded > 0 ? (
              <p className="overview-revenue-hero__warn">
                {metrics.monthlyUsdExcluded} org
                {metrics.monthlyUsdExcluded === 1 ? "" : "s"} missing monthly
                price (Enterprise / quote).
              </p>
            ) : null}
          </section>

          <section
            className="platform-section-card"
            aria-label="Plans breakdown"
          >
            <h2 className="platform-section-card__title">By plan (live)</h2>
            <p className="platform-section-card__subtitle mb-0">
              Organizations with a non-expired subscription.
            </p>
            <div className="overview-plan-chips mt-2">
              {byPlanEntries.length === 0 ? (
                <span className="text-muted">No live plans</span>
              ) : (
                byPlanEntries
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([planKey, n]) => (
                    <span key={planKey} className="overview-plan-chip">
                      <span className="overview-plan-chip__name">{planKey}</span>
                      <span className="overview-plan-chip__count">{n}</span>
                    </span>
                  ))
              )}
            </div>
          </section>

          <section
            className="platform-section-card"
            aria-label="Intelligence adoption"
          >
            <h2 className="platform-section-card__title">Intelligence</h2>
            <p className="platform-section-card__subtitle mb-0">
              Add-on capacity on live orgs · {usdFormatter.format(intel.monthlyUsd)}{" "}
              est. Intelligence MRR (paid grants only).
            </p>
            <div className="overview-metrics-grid mt-3" aria-label="Intelligence metrics">
              <MetricTile
                accent="live"
                label="With Intelligence"
                value={intel.total}
                hint="Live orgs with capacity"
              />
              <MetricTile
                accent="paying"
                label="Paid / grant"
                value={intel.active}
                hint="Not on Intelligence trial"
              />
              <MetricTile
                accent="trial"
                label="Intel trial"
                value={intel.trialing}
                hint="Trying Intelligence"
              />
              <MetricTile
                accent="premium"
                label="Extra"
                value={intel.byCapacity?.extra ?? 0}
                hint="On Extra capacity"
              />
            </div>
            <div className="overview-plan-chips mt-2">
              {byIntelEntries.length === 0 ? (
                <span className="text-muted">No Intelligence adoption yet</span>
              ) : (
                byIntelEntries
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([cap, n]) => (
                    <span key={cap} className="overview-plan-chip">
                      <span className="overview-plan-chip__name">{cap}</span>
                      <span className="overview-plan-chip__count">{n}</span>
                    </span>
                  ))
              )}
            </div>
          </section>

          <section aria-label="Upcoming renewals">
            <div className="overview-section-head">
              <h2 className="overview-section-head__title">Upcoming renewals</h2>
              {metrics.renewals.length > 0 ? (
                <span className="overview-section-head__count">
                  {metrics.renewals.length} org
                  {metrics.renewals.length === 1 ? "" : "s"}
                </span>
              ) : null}
            </div>

            {metrics.renewals.length === 0 ? (
              <div className="overview-empty platform-section-card">
                No live subscriptions with a plan in this environment.
              </div>
            ) : (
              <div className="overview-renewals-list">
                {metrics.renewals.map((org) => (
                  <RenewalCard key={org.id} org={org} />
                ))}
              </div>
            )}

            {revenueRenewals.length > 0 &&
            revenueRenewals.length < metrics.renewals.length ? (
              <p className="overview-footnote">
                {metrics.renewals.length - revenueRenewals.length} shown above
                are live but not in revenue (trial or VES grace).
              </p>
            ) : null}
          </section>
        </>
      )}
    </div>
  );
};

export default OverviewPage;
